import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { IBenefitPlanBasicsState } from '@/hooks/useBenefitPlanBasics';
import type { IInsuranceProvider } from '@/@types/insurance';
import type { IOrgInsuranceType } from '@/@types/insurance';

interface IPlanDetailsCardProps {
  state: IBenefitPlanBasicsState;
  providers: IInsuranceProvider[] | undefined;
  orgTypes: IOrgInsuranceType[] | undefined;
  setField: <K extends keyof IBenefitPlanBasicsState>(
    key: K,
    value: IBenefitPlanBasicsState[K]
  ) => void;
}

export function PlanDetailsCard({ state, providers, orgTypes, setField }: IPlanDetailsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Plan Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label>
              Plan Name <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="e.g. Premium Health Plan 2024"
              value={state.name}
              onChange={e => setField('name', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Provider <span className="text-destructive">*</span>
            </Label>
            <Select value={state.providerId} onValueChange={v => setField('providerId', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers?.map(p => (
                  <SelectItem key={p._id} value={p._id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>
              Insurance Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={state.insuranceTypeId}
              onValueChange={v => setField('insuranceTypeId', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {orgTypes?.map(t => (
                  <SelectItem key={t._id} value={t._id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select value={state.currency} onValueChange={v => setField('currency', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">EUR — Euro</SelectItem>
                <SelectItem value="USD">USD — US Dollar</SelectItem>
                <SelectItem value="GBP">GBP — British Pound</SelectItem>
                <SelectItem value="CHF">CHF — Swiss Franc</SelectItem>
                <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                <SelectItem value="SAR">SAR — Saudi Riyal</SelectItem>
                <SelectItem value="QAR">QAR — Qatari Riyal</SelectItem>
                <SelectItem value="BHD">BHD — Bahraini Dinar</SelectItem>
                <SelectItem value="KWD">KWD — Kuwaiti Dinar</SelectItem>
                <SelectItem value="OMR">OMR — Omani Rial</SelectItem>
                <SelectItem value="JOD">JOD — Jordanian Dinar</SelectItem>
                <SelectItem value="EGP">EGP — Egyptian Pound</SelectItem>
                <SelectItem value="TRY">TRY — Turkish Lira</SelectItem>
                <SelectItem value="ILS">ILS — Israeli Shekel</SelectItem>
                <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                <SelectItem value="ZAR">ZAR — South African Rand</SelectItem>
                <SelectItem value="SGD">SGD — Singapore Dollar</SelectItem>
                <SelectItem value="HKD">HKD — Hong Kong Dollar</SelectItem>
                <SelectItem value="AUD">AUD — Australian Dollar</SelectItem>
                <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Area of Coverage</Label>
            <Input
              placeholder="e.g. Worldwide"
              value={state.areaOfCoverage}
              onChange={e => setField('areaOfCoverage', e.target.value)}
            />
          </div>
        </div>
        <Separator />
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Max Annual Benefit</Label>
            <Input
              type="number"
              placeholder="0"
              value={state.maxAnnualBenefit}
              onChange={e => setField('maxAnnualBenefit', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Annual Deductible</Label>
            <Input
              type="number"
              placeholder="0"
              value={state.annualDeductible}
              onChange={e => setField('annualDeductible', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Plan Pays %</Label>
            <Input
              type="number"
              placeholder="100"
              value={state.coinsurancePlanPays}
              onChange={e => setField('coinsurancePlanPays', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>You Pay %</Label>
            <Input
              type="number"
              placeholder="0"
              value={state.coinsuranceYouPay}
              onChange={e => setField('coinsuranceYouPay', e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Description</Label>
          <Textarea
            placeholder="Optional description or notes"
            value={state.description}
            onChange={e => setField('description', e.target.value)}
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  );
}
